import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  Space,
  Button,
  Typography,
  Divider,
  Row,
  Col,
  Steps,
  Card,
  Radio,
  message,
} from "antd";
import { DeleteOutlined, TeamOutlined, CalendarOutlined } from "@ant-design/icons";
import { RadioCardGroup } from "../../frontdesk/components/RadioCardGroup";
import { canViewPrices } from '../../../utils/permissions';

const { Text, Title } = Typography;


interface CreatePosOrderFormProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  tables: any[];
  menuItems: any[];
  categories?: any[];
  checkedInBookings?: any[];
  initialData?: any;
}

export const CreatePosOrderForm: React.FC<CreatePosOrderFormProps> = ({
  visible,
  onCancel,
  onSubmit,
  tables,
  menuItems,
  categories = [],
  checkedInBookings = [],
  initialData,
}) => {
  const [form] = Form.useForm();
  const orderType = Form.useWatch("order_type", form);

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [updateTrigger, setUpdateTrigger] = useState(0);

  useEffect(() => {
    if (visible && initialData) {
      form.setFieldsValue({
        order_type: initialData.order_type,
        table_ids: initialData.table_ids || (initialData.table_id ? [initialData.table_id] : []),
        booking_id: initialData.booking_id,
        room_id: initialData.room_id,
        guest_name: initialData.guest_name,
        promotion_code: initialData.promotion_code,
        items: initialData.items ? initialData.items.map((i: any) => ({
          menu_item_id: i.menu_item_id,
          quantity: i.quantity,
          note: i.note,
          no_of_guests: i.no_of_guests || 1,
          no_of_days: i.no_of_days || 1
        })) : [],
        discount_type: initialData.discount_type || (initialData.discount_amount ? "fixed" : "none"),
        discount_value: initialData.discount_value || initialData.discount_amount || 0,
        service_charge_type: initialData.service_charge_type || (initialData.service_charge ? "fixed" : "none"),
        service_charge_value: initialData.service_charge_value || initialData.service_charge || 0,
        tax_type: initialData.tax_type || (initialData.tax_amount ? "fixed" : "none"),
        tax_value: initialData.tax_value || initialData.tax_amount || 0,
      });
      setUpdateTrigger((prev) => prev + 1);
    } else if (visible && !initialData) {
      form.resetFields();
      setCurrentStep(0);
      setSelectedCategory("");
      setSearchTerm("");
      setUpdateTrigger((prev) => prev + 1);
    } else {
      form.resetFields();
      setCurrentStep(0);
      setSelectedCategory("");
      setSearchTerm("");
    }
  }, [visible, initialData, form]);

  useEffect(() => {
    if (visible && categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0].id);
    }
  }, [visible, categories, selectedCategory]);

  const previousOrderTypeRef = React.useRef(orderType);
  useEffect(() => {
    if (previousOrderTypeRef.current !== undefined && previousOrderTypeRef.current !== orderType) {
      form.setFieldsValue({
        table_ids: [],
        booking_id: undefined,
        room_id: undefined,
        guest_name: undefined,
      });
    }
    previousOrderTypeRef.current = orderType;
  }, [orderType, form]);

  const RoomCardGroup = ({
    value,
    onChange,
  }: {
    value?: string;
    onChange?: (v: string) => void;
  }) => {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        {checkedInBookings.length === 0 ? (
          <Text type="secondary">No occupied rooms available.</Text>
        ) : (
          checkedInBookings.map((b) => {
            const isSelected = value === b.id;
            return (
              <div
                key={b.id}
                onClick={() => {
                  if (onChange) onChange(b.id);
                  form.setFieldsValue({
                    guest_name: b.guest_name,
                    room_id: b.room_id,
                  });
                }}
                style={{
                  padding: "10px 16px",
                  border: `1px solid ${isSelected ? "#B79A68" : "#d9d9d9"}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: isSelected ? "#B79A68" : "#fff",
                  color: isSelected ? "#fff" : "#000",
                  fontWeight: isSelected ? 600 : 400,
                  transition: "all 0.2s",
                  flex: "1 1 auto",
                  textAlign: "center",
                  userSelect: "none",
                }}
              >
                Room {b.room_number || "N/A"}{" "}
                {b.room_type_name ? `(${b.room_type_name})` : ""} <br />
                <span style={{ fontSize: "12px", opacity: 0.8 }}>
                  {b.guest_name}
                </span>
              </div>
            );
          })
        )}
      </div>
    );
  };

  const filteredMenuItems = useMemo(() => {
    let result = menuItems.filter((item: any) => item.is_active !== false);
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter((item: any) =>
        item.name.toLowerCase().includes(lower),
      );
    } else if (selectedCategory && selectedCategory !== "all") {
      result = result.filter(
        (item: any) => item.category_id === selectedCategory,
      );
    }
    return result;
  }, [menuItems, selectedCategory, searchTerm]);

  const handleMealClick = (meal: any) => {
    const currentItems = form.getFieldValue("items") || [];
    const existingIndex = currentItems.findIndex(
      (it: any) => it.menu_item_id === meal.id,
    );

    if (existingIndex >= 0) {
      const newItems = [...currentItems];
      newItems[existingIndex].quantity =
        (newItems[existingIndex].quantity || 0) + 1;
      form.setFieldsValue({ items: newItems });
    } else {
      form.setFieldsValue({
        items: [
          ...currentItems,
          { menu_item_id: meal.id, quantity: 1, no_of_guests: 1, no_of_days: 1, note: "" },
        ],
      });
    }
    setUpdateTrigger((prev) => prev + 1);
  };

  const subtotal = useMemo(() => {
    let sum = 0;
    const items = form.getFieldValue("items") || [];
    items.forEach((it: any) => {
      if (it?.menu_item_id && it?.quantity) {
        const itemObj = menuItems.find((m: any) => m.id === it.menu_item_id);
        if (itemObj) {
          const price = orderType === "take_away" ? Number(itemObj.price_take_away) : Number(itemObj.price_dine_in);
          const guests = it.no_of_guests || 1;
          const days = it.no_of_days || 1;
          sum += price * Number(it.quantity) * guests * days;
        }
      }
    });
    return Math.round(sum);
  }, [updateTrigger, menuItems, orderType]);

  const {
    discountAmount,
    serviceAmount,
    taxAmount,
    total2,
    total3,
    finalTotal,
  } = useMemo(() => {
    const dType = form.getFieldValue("discount_type") || "none";
    const dVal = Number(form.getFieldValue("discount_value") || 0);
    const dAmt = Math.round(
      dType === "percentage"
        ? subtotal * (dVal / 100)
        : dType === "fixed"
          ? dVal
          : 0,
    );

    const t2 = Math.max(0, subtotal - dAmt);

    const sType = form.getFieldValue("service_charge_type") || "none";
    const sVal = Number(form.getFieldValue("service_charge_value") || 0);
    const sAmt = Math.round(
      sType === "percentage" ? t2 * (sVal / 100) : sType === "fixed" ? sVal : 0,
    );

    const t3 = t2 + sAmt;

    const xType = form.getFieldValue("tax_type") || "none";
    const xVal = Number(form.getFieldValue("tax_value") || 0);
    const xAmt = Math.round(
      xType === "percentage" ? t3 * (xVal / 100) : xType === "fixed" ? xVal : 0,
    );

    return {
      discountAmount: dAmt,
      serviceAmount: sAmt,
      taxAmount: xAmt,
      total2: t2,
      total3: t3,
      finalTotal: t3 + xAmt,
    };
  }, [updateTrigger, subtotal]);

  const handleFinish = (values: any) => {
    onSubmit({
      ...values,
      discount_amount: discountAmount,
      service_charge: serviceAmount,
      tax_amount: taxAmount,
    });
  };

  const TableCardGroup = ({
    value = [],
    onChange,
  }: {
    value?: string[];
    onChange?: (v: string[]) => void;
  }) => {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        {tables.map((t) => {
          const isSelected = value.includes(t.id);
          const isDisabled = t.status !== "available";
          return (
            <div
              key={t.id}
              onClick={() => {
                if (!isDisabled && onChange) {
                  const newVal = isSelected
                    ? value.filter((v) => v !== t.id)
                    : [...value, t.id];
                  onChange(newVal);
                }
              }}
              style={{
                padding: "10px 16px",
                border: `1px solid ${isSelected ? (isDisabled ? "#d9d9d9" : "#B79A68") : "#d9d9d9"}`,
                borderRadius: "8px",
                cursor: isDisabled ? "not-allowed" : "pointer",
                backgroundColor: isSelected
                  ? isDisabled
                    ? "#f5f5f5"
                    : "#B79A68"
                  : isDisabled
                    ? "#f5f5f5"
                    : "#fff",
                color: isSelected
                  ? isDisabled
                    ? "#a6a6a6"
                    : "#fff"
                  : isDisabled
                    ? "#a6a6a6"
                    : "#000",
                fontWeight: isSelected ? 600 : 400,
                transition: "all 0.2s",
                flex: "1 1 auto",
                textAlign: "center",
                userSelect: "none",
                opacity: isDisabled ? 0.6 : 1,
              }}
            >
              {t.table_number} {isDisabled && `(${t.status})`}
            </div>
          );
        })}
      </div>
    );
  };

  const step1Content = (
    <div>
      <div style={{ backgroundColor: "#fafafa", padding: 16, borderRadius: 8 }}>
        <Form.Item
          name="order_type"
          label={<Text strong>Order Type</Text>}
          rules={[{ required: true, message: "Order type is required" }]}
        >
          <RadioCardGroup
            options={[
              { label: "Dine In", value: "dine_in" },
              { label: "Room Service", value: "room_service" },
              { label: "Take Away", value: "take_away" },
            ]}
          />
        </Form.Item>

        {orderType === "dine_in" && (
          <Form.Item
            name="table_ids"
            label={<Text strong>Dining Tables</Text>}
            rules={[
              { required: true, message: "Please select at least one table" },
            ]}
          >
            <TableCardGroup />
          </Form.Item>
        )}

        {orderType === "room_service" && (
          <Form.Item
            name="booking_id"
            label={<Text strong>Occupied Rooms</Text>}
            rules={[{ required: true, message: "Please select a room" }]}
          >
            <RoomCardGroup />
          </Form.Item>
        )}

        <Form.Item
          name="guest_name"
          label={<Text strong>Guest Name</Text>}
          rules={[{ required: true, message: "Guest name is required" }]}
        >
          <Input placeholder="Enter guest name" size="large" />
        </Form.Item>

        <Form.Item name="room_id" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="promotion_code"
          label={<Text strong>Promotion Code (Optional)</Text>}
          style={{ marginBottom: 0 }}
        >
          <Input placeholder="e.g. PROMO2026" size="large" />
        </Form.Item>
      </div>

      <div style={{ textAlign: "right", marginTop: 24 }}>
        <Button
          type="primary"
          size="large"
          disabled={
            orderType === "room_service" && checkedInBookings.length === 0
          }
          onClick={() => {
            const fieldsToValidate = ["order_type", "guest_name"];
            if (orderType === "room_service")
              fieldsToValidate.push("booking_id");
            if (orderType === "dine_in") fieldsToValidate.push("table_ids");

            form
              .validateFields(fieldsToValidate)
              .then(() => {
                setCurrentStep(1);
              })
              .catch(() => {
                // Validation failed, UI will show errors automatically
              });
          }}
          style={{
            backgroundColor: "#B79A68",
            borderColor: "#B79A68",
            width: 200,
          }}
        >
          Next: Select Meals
        </Button>
      </div>
    </div>
  );

  const step2Content = (
    <Row gutter={24} style={{ minHeight: "65vh" }}>
      <Col span={14} style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="Search meals..."
            allowClear
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="large"
            style={{ width: "100%" }}
          />
        </div>
        <div
          style={{
            display: "flex",
            gap: 12,
            overflowX: "auto",
            paddingBottom: 16,
            marginBottom: 16,
            borderBottom: "1px solid #f0f0f0",
            flexShrink: 0,
          }}
        >
          {categories?.map((cat: any) => (
            <Button
              key={cat.id}
              type={selectedCategory === cat.id ? "primary" : "default"}
              shape="round"
              onClick={() => setSelectedCategory(cat.id)}
              style={
                selectedCategory === cat.id
                  ? { backgroundColor: "#B79A68", borderColor: "#B79A68" }
                  : {}
              }
            >
              {cat.name}
            </Button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", paddingRight: 8 }}>
          <Row gutter={[16, 16]}>
            {filteredMenuItems.map((meal: any) => (
              <Col span={8} key={meal.id}>
                <Card
                  hoverable
                  bodyStyle={{ padding: 12, textAlign: "center" }}
                  onClick={() => handleMealClick(meal)}
                  style={{
                    borderRadius: 12,
                    overflow: "hidden",
                    height: "100%",
                    border: "1px solid #e8e8e8",
                  }}
                >
                  <img
                    src={
                      meal.image_url ||
                      "https://placehold.co/200x200?text=No+Image"
                    }
                    alt={meal.name}
                    style={{
                      width: "100%",
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 8,
                      marginBottom: 12,
                    }}
                  />
                  <Text
                    strong
                    style={{
                      display: "block",
                      fontSize: 14,
                      marginBottom: 4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {meal.name}
                  </Text>
                  {canViewPrices() && (
                    <Text strong style={{ color: "#B79A68", fontSize: 16 }}>
                      LKR{" "}
                      {Number(orderType === "take_away" ? meal.price_take_away : meal.price_dine_in).toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </Text>
                  )}
                </Card>
              </Col>
            ))}
            {filteredMenuItems.length === 0 && (
              <div
                style={{
                  width: "100%",
                  textAlign: "center",
                  padding: "40px",
                  color: "#999",
                }}
              >
                No meals found in this category.
              </div>
            )}
          </Row>
        </div>
      </Col>

      <Col span={10} style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{
            backgroundColor: "#fafafa",
            borderRadius: 12,
            padding: 20,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            border: "1px solid #f0f0f0",
            height: "65vh",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              flexShrink: 0,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              Current Order
            </Title>
            <Text type="secondary">
              {(form.getFieldValue("items") || []).reduce(
                (acc: number, item: any) => acc + (Number(item.quantity) || 0),
                0,
              )}{" "}
              items
            </Text>
          </div>

          <div
            style={{
              flex: 1,
              overflowY: "auto",
              paddingRight: 8,
              marginBottom: 16,
            }}
          >
            <Form.List name="items">
              {(fields, { remove }) => (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  {fields.map((field) => {
                    const itemData = form.getFieldValue(["items", field.name]);
                    const meal = menuItems.find(
                      (m: any) => m.id === itemData?.menu_item_id,
                    );
                    if (!meal) return null;

                    return (
                      <div
                        key={field.key}
                        style={{
                          backgroundColor: "#fff",
                          padding: 12,
                          borderRadius: 8,
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        <div
                          style={{ display: "flex", gap: 12, marginBottom: 8 }}
                        >
                          {meal.image_url && (
                            <img
                              src={meal.image_url}
                              alt={meal.name}
                              style={{
                                width: 48,
                                height: 48,
                                objectFit: "cover",
                                borderRadius: 6,
                              }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Text strong style={{ fontSize: 14 }}>
                                {meal.name}
                              </Text>
                              <Text strong>
                                {canViewPrices()
                                  ? `LKR ${(
                                      Number(orderType === "take_away" ? meal.price_take_away : meal.price_dine_in) *
                                      Number(itemData?.quantity || 1) *
                                      (orderType === "room_service" ? Number(itemData?.no_of_guests || 1) : 1) *
                                      (orderType === "room_service" ? Number(itemData?.no_of_days || 1) : 1)
                                    ).toLocaleString(undefined, {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}`
                                  : ""}
                              </Text>
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 12,
                            alignItems: "flex-start",
                          }}
                        >
                          <Form.Item
                            {...field}
                            name={[field.name, "menu_item_id"]}
                            hidden
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, "quantity"]}
                            style={{ margin: 0, width: 80 }}
                            tooltip="Quantity"
                          >
                            <InputNumber min={1} style={{ width: "100%" }} />
                          </Form.Item>
                          {orderType === "room_service" && (
                            <>
                              <Form.Item
                                {...field}
                                name={[field.name, "no_of_guests"]}
                                style={{ margin: 0, width: 80 }}
                                tooltip="Guests"
                              >
                                <InputNumber min={1} style={{ width: "100%" }} addonBefore={<TeamOutlined />} />
                              </Form.Item>
                              <Form.Item
                                {...field}
                                name={[field.name, "no_of_days"]}
                                style={{ margin: 0, width: 80 }}
                                tooltip="Days"
                              >
                                <InputNumber min={1} style={{ width: "100%" }} addonBefore={<CalendarOutlined />} />
                              </Form.Item>
                            </>
                          )}
                          <Form.Item
                            {...field}
                            name={[field.name, "note"]}
                            style={{ margin: 0, flex: 1 }}
                          >
                            <Input placeholder="Note..." />
                          </Form.Item>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              remove(field.name);
                              // Add a small delay to allow Form to update its state before triggering recalculation
                              setTimeout(
                                () => setUpdateTrigger((prev) => prev + 1),
                                0,
                              );
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {fields.length === 0 && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 0",
                        color: "#999",
                      }}
                    >
                      No items in the order yet. Click a meal to add it.
                    </div>
                  )}
                </div>
              )}
            </Form.List>
          </div>

          <Divider style={{ margin: "0 0 16px 0", flexShrink: 0 }} />

          <div style={{ marginBottom: 16, flexShrink: 0 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Text style={{ width: 100 }}>Discount:</Text>
                <Form.Item name="discount_type" noStyle initialValue="none">
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="none">None</Radio.Button>
                    <Radio.Button value="fixed">Fixed</Radio.Button>
                    <Radio.Button value="percentage">Percentage</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                {form.getFieldValue("discount_type") !== "none" && (
                  <Form.Item name="discount_value" noStyle>
                    <InputNumber
                      size="small"
                      style={{ flex: 1 }}
                      min={0}
                      addonAfter={
                        form.getFieldValue("discount_type") === "percentage"
                          ? "%"
                          : "LKR"
                      }
                    />
                  </Form.Item>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Text style={{ width: 100 }}>Service Chg:</Text>
                <Form.Item
                  name="service_charge_type"
                  noStyle
                  initialValue="none"
                >
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="none">None</Radio.Button>
                    <Radio.Button value="fixed">Fixed</Radio.Button>
                    <Radio.Button value="percentage">Percentage</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                {form.getFieldValue("service_charge_type") !== "none" && (
                  <Form.Item name="service_charge_value" noStyle>
                    <InputNumber
                      size="small"
                      style={{ flex: 1 }}
                      min={0}
                      addonAfter={
                        form.getFieldValue("service_charge_type") ===
                        "percentage"
                          ? "%"
                          : "LKR"
                      }
                    />
                  </Form.Item>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Text style={{ width: 100 }}>VAT Setup:</Text>
                <Form.Item name="tax_type" noStyle initialValue="none">
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="none">None</Radio.Button>
                    <Radio.Button value="percentage">Enable</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                {form.getFieldValue("tax_type") !== "none" && (
                  <Form.Item name="tax_value" noStyle>
                    <InputNumber
                      size="small"
                      style={{ flex: 1 }}
                      min={0}
                      max={100}
                      addonAfter="%"
                    />
                  </Form.Item>
                )}
              </div>
            </div>
          </div>

          <Divider style={{ margin: "0 0 16px 0", flexShrink: 0 }} />

          {canViewPrices() && (
          <div style={{ marginBottom: 16, flexShrink: 0 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <Text type="secondary">Subtotal</Text>
              <Text>
                LKR{" "}
                {subtotal.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Text>
            </div>
            {discountAmount > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                  color: "#52c41a",
                }}
              >
                <Text style={{ color: "inherit" }}>Discount</Text>
                <Text style={{ color: "inherit" }}>
                  - LKR{" "}
                  {discountAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </Text>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <Text type="secondary">Service Charge</Text>
              <Text>
                LKR{" "}
                {serviceAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Text>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <Text type="secondary">Tax / VAT</Text>
              <Text>
                LKR{" "}
                {taxAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Text>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 8,
              }}
            >
              <Text strong style={{ fontSize: 18 }}>
                Payable Amount
              </Text>
              <Text strong style={{ fontSize: 18, color: "#B79A68" }}>
                LKR{" "}
                {finalTotal.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Text>
            </div>
          </div>
          )}

          <div style={{ display: "flex", gap: 12, flexShrink: 0 }}>
            <Button
              size="large"
              style={{ flex: 1 }}
              onClick={() => setCurrentStep(0)}
            >
              Back
            </Button>
            <Button
              size="large"
              type="primary"
              disabled={(form.getFieldValue("items") || []).length === 0}
              onClick={() => form.submit()}
              style={{
                flex: 2,
                backgroundColor:
                  (form.getFieldValue("items") || []).length === 0
                    ? undefined
                    : "#52c41a",
                borderColor:
                  (form.getFieldValue("items") || []).length === 0
                    ? undefined
                    : "#52c41a",
              }}
            >
              Create Order
            </Button>
          </div>
        </div>
      </Col>
    </Row>
  );

  return (
    <Modal
      title={null}
      open={visible}
      width="70vw"
      onCancel={onCancel}
      footer={null}
      centered
      destroyOnClose
      style={{ top: 10 }}
      styles={{ body: { padding: "0px" } }}
    >
      <div>
        <Title level={4} style={{ marginBottom: 16 }}>
          Create POS Order
        </Title>
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #e8e8e8",
            marginBottom: 20,
          }}
        >
          {["Order Details", "Meal Selection & Payment"].map((title, index) => {
            const isActive = currentStep === index;
            return (
              <div
                key={index}
                style={{
                  padding: "8px 24px",
                  color: isActive ? "#B79A68" : "#888",
                  borderBottom: isActive
                    ? "2px solid #B79A68"
                    : "2px solid transparent",
                  fontWeight: isActive ? 600 : 400,
                  fontSize: 15,
                  marginBottom: -1,
                }}
              >
                {title}
              </div>
            );
          })}
        </div>

        <Form
          form={form}
          layout="vertical"
          onValuesChange={() => setUpdateTrigger((prev) => prev + 1)}
          onFinish={handleFinish}
          initialValues={{
            order_type: "dine_in",
            items: [],
            discount_type: "none",
            service_charge_type: "none",
            tax_type: "none",
            table_ids: [],
          }}
        >
          <div style={{ display: currentStep === 0 ? "block" : "none" }}>
            {step1Content}
          </div>
          <div style={{ display: currentStep === 1 ? "block" : "none" }}>
            {step2Content}
          </div>
        </Form>
      </div>
    </Modal>
  );
};
